/** Backup tab: how many cloud backups to keep per brand + who gets told when a backup fails or goes stale. */
import { useEffect, useState } from "react";
import { BellRing, History, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function RetentionAndAlerts({ s, save, onAlertSent }) {
  const [keep, setKeep] = useState(s.keep ?? 7);
  const [emails, setEmails] = useState((s.alert_emails || []).join(", "));
  const [testing, setTesting] = useState(false);
  useEffect(() => { setKeep(s.keep ?? 7); setEmails((s.alert_emails || []).join(", ")); }, [s.keep, s.alert_emails]);

  const saveKeep = () => { const n = Math.max(0, parseInt(keep, 10) || 0); if (n !== s.keep) save({ keep: n }, n ? `Keeping the last ${n} backups per brand` : "Keeping every backup (no clean-up)"); };
  const saveEmails = () => { if (emails !== (s.alert_emails || []).join(", ")) save({ alert_emails: emails }, "Alert e-mails saved"); };
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/cloud-backup/alert-test");
      const bits = [`${data.pushed} push notification(s)`, data.emailed?.length ? `e-mailed ${data.emailed.join(", ")}` : null, data.email_failed?.length ? `e-mail failed: ${data.email_failed.join(", ")}${data.email_reason ? ` (${data.email_reason})` : ""}` : null].filter(Boolean);
      (data.email_failed?.length ? toast.warning : toast.success)(`Test alert sent — ${bits.join("; ")}`);
      onAlertSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send test alert"); }
    finally { setTesting(false); }
  };
  const la = s.last_alert;

  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="cloud-backup-retention-alerts">
      <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/70">
        <p className="flex items-center gap-2 font-semibold text-white/80"><History className="h-4 w-4 text-sky-300" /> Retention</p>
        <p className="mt-1 text-white/40">After each backup, older zips on the drive are moved to the recycle bin so only the newest ones stay.</p>
        <label className="mt-2 inline-flex items-center gap-2">Keep the last
          <input type="number" min="0" max="365" value={keep} onChange={(e) => setKeep(e.target.value)} onBlur={saveKeep} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            className="w-16 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-center text-white" data-testid="cloud-backup-keep" />
          backups per brand <span className="text-white/40">(0 = never delete)</span>
        </label>
      </div>
      <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/70">
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-amber-300" />
          <p className="flex-1 font-semibold text-white/80">Alerts — backup failed, or hasn't run in 2 days</p>
          <button onClick={() => save({ alert_push: !s.alert_push }, s.alert_push ? "Push alerts OFF" : "Push alerts ON")} aria-pressed={!!s.alert_push}
            className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${s.alert_push ? "bg-emerald-600 text-white" : "bg-white/10 text-white/60"}`} data-testid="cloud-backup-alert-push">Push {s.alert_push ? "ON" : "OFF"}</button>
        </div>
        <p className="mt-1 text-white/40">Push goes to owners, admins and the superadmin through EliteBot. E-mails are sent from the superadmin mailbox (Settings → Email).</p>
        <div className="mt-2 flex gap-2">
          <input value={emails} onChange={(e) => setEmails(e.target.value)} onBlur={saveEmails} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} placeholder="alerts@company.com, owner@company.com"
            className="flex-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-white placeholder:text-white/25" data-testid="cloud-backup-alert-emails" />
          <button onClick={sendTest} disabled={testing} className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-2.5 py-1 font-semibold text-white hover:bg-white/15 disabled:opacity-50" data-testid="cloud-backup-alert-test">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test
          </button>
        </div>
        {la && (
          <p className="mt-2 text-white/50" data-testid="cloud-backup-last-alert">
            Last alert: <span className="text-white/70">{la.title}</span> · {new Date(la.at).toLocaleString()} · {la.pushed} push{la.emailed?.length ? `, e-mailed ${la.emailed.length}` : ""}{la.email_failed?.length ? <span className="text-amber-300">, {la.email_failed.length} e-mail failed{la.email_reason ? ` — ${la.email_reason}` : ""}</span> : ""}
          </p>
        )}
      </div>
    </div>
  );
}
